import React, { useMemo } from 'react'
import { useHistory } from 'react-router'
import { Form as FinalForm } from 'react-final-form'
import { Form, Header, DropdownProps, Segment, Button } from 'semantic-ui-react'
import { toast } from 'react-toastify'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { FORM_ERROR } from 'final-form'

import { API_LOCALIZATIONS, PATH_PRODUCTS_EDIT } from '../../consts'
import { to } from '../../utils/to'
import { handleGenericErrors, handleNotFoundErrors } from '../../utils/Products/errorHandler'
import { Product } from '../../hooks/useProduct'
import { formatUpdatePayload } from './form/formatPayload'
import { updateLocalization } from './api/update'
import { getInitialValues } from './form/getInitialValues'
import { Localization } from '../../hooks/useLocalizations'
import { EditLocalizationFormType } from './form/types'
import { mutators } from '../../utils/mutators'
import { hasAnyChangesFromDefault, validateEdit } from './form/validation'
import { localizationListToSubdivisionOptions } from '../../utils/localizations/localizationOptions'
import { EditLocalizationForm } from './LocalizationForm/EditLocalizationForm'

export const EditLocalization = ({
  localizations,
  product,
  currentLocalization,
  refetchLocalizations,
}: {
  localizations?: Localization[]
  product: Product
  currentLocalization?: Localization
  refetchLocalizations: () => void
}) => {
  const history = useHistory()

  const initialLocalizationFormValues = useMemo(() => {
    if (currentLocalization) {
      return getInitialValues(product, currentLocalization)
    } else {
      return {}
    }
  }, [currentLocalization, product])

  const subdivisionOptions = localizationListToSubdivisionOptions(localizations)

  const subdivisionSelectorOnChange = (event: any, data: DropdownProps) => {
    const { value: selectedLocalizationId } = data
    history.push(
      `${PATH_PRODUCTS_EDIT}/${product?.id}/localizations/${selectedLocalizationId}${history.location.search}`
    )
  }

  const handleRemove = async () => {
    const axiosRequestObj: AxiosRequestConfig = {
      url: `${API_LOCALIZATIONS}/${currentLocalization?.id}`,
      method: 'DELETE',
      withCredentials: true,
    }

    const [error, response] = await to<AxiosResponse, AxiosError>(axios(axiosRequestObj))

    if (response) {
      toast(`State ${currentLocalization?.country}-${currentLocalization?.subdivision} deleted`, {
        containerId: 'default',
      })
      refetchLocalizations()
      history.replace(`${PATH_PRODUCTS_EDIT}/${product?.id}`)
    } else if (error) {
      handleGenericErrors(error)
    }
  }

  const onSubmit = async (values: EditLocalizationFormType) => {
    if (!product) return

    if (!hasAnyChangesFromDefault(values, product)) {
      return {
        [FORM_ERROR]: {
          name: 'Change Required',
          message:
            'At least one field must be changed from the default values. Remove this state if its custom content should no longer exist.',
        },
      }
    }

    const payload = formatUpdatePayload(values, product)

    const [error, response] = await updateLocalization(values.id, payload)

    const state = `${values.country}-${values.subdivision}`

    if (response) {
      toast(`State ${state} updated.`, {
        containerId: 'default',
      })
    } else if (error) {
      if (error.response?.status === 404) {
        handleNotFoundErrors(`State ${state}`, error, 'state could not be updated')
      } else if (error.response?.status === 400) {
        const customMessage = `State ${state} could not be updated due to the following: ${error.response?.data?.errors}`

        handleGenericErrors(error, customMessage)
      } else {
        handleGenericErrors(error)
      }
    }
  }

  return (
    <>
      <Segment
        attached="top"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Header as="h3" style={{ margin: 0 }}>
          State-Specific Content
        </Header>

        <Button
          positive
          onClick={() =>
            history.push(
              `${PATH_PRODUCTS_EDIT}/${product?.id}/localizations/new${history.location.search}`
            )
          }
        >
          Add State
        </Button>
      </Segment>

      {localizations && localizations.length > 0 ? (
        <>
          <Segment attached>
            <Form>
              <Form.Dropdown
                label="Select a state:"
                selection
                inline
                placeholder="State"
                options={subdivisionOptions}
                onChange={subdivisionSelectorOnChange}
                value={currentLocalization?.id}
              />
            </Form>
          </Segment>

          <FinalForm
            mutators={mutators}
            initialValues={initialLocalizationFormValues}
            onSubmit={onSubmit}
            validate={validateEdit}
          >
            {() => (
              <>
                <EditLocalizationForm product={product} />
                <Segment attached="bottom" color="red">
                  <b>Remove this state</b>
                  <br />
                  <p>
                    Once you remove the custom content, there's no going back. Please be certain.
                  </p>
                  <Button basic negative size="tiny" type="button" onClick={handleRemove}>
                    Remove
                  </Button>
                </Segment>
              </>
            )}
          </FinalForm>
        </>
      ) : (
        <>
          <Segment attached>
            <p>No state-specific content to display for this product</p>
          </Segment>
        </>
      )}
    </>
  )
}
